import { Link } from '@tanstack/react-router';
import { FolderPlus, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Popover, usePopover } from '../../../components/Popover';
import { errorMessage } from '../../../lib/api-client';
import { formatDate } from '../../../lib/format';
import { useCanView } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { poolsApi, useCandidatePools } from '../api/pools-api';
import { AddToPoolPanel, usePoolRefresh } from './PoolPanels';
import styles from '../Pools.module.css';

/**
 * CAND-02's pools (S25): where this person sits, until when, and — for anyone who works with
 * pools — "Add to pool" and remove. Q6: a person added with no job is already in one.
 */
export function CandidatePoolsCard({ candidateId, name }: { readonly candidateId: string; readonly name: string }): ReactNode {
  const canView = useCanView('talent_pool');
  const pools = useCandidatePools(candidateId, canView);
  const refresh = usePoolRefresh();
  const show = useToasts((state) => state.show);
  const showUndo = useToasts((state) => state.showUndo);
  const { anchor, triggerRef, toggle, close } = usePopover();

  if (!canView) return null;
  const rows = pools.data?.pools ?? [];
  const canEdit = pools.data?.canEdit ?? false;

  const remove = async (membershipId: string, poolId: string, poolName: string): Promise<void> => {
    try {
      await poolsApi.removeMembers(poolId, { membershipIds: [membershipId] });
      await refresh();
      showUndo(`Removed from ${poolName}`, () => {
        void poolsApi
          .addMembers(poolId, { candidateIds: [candidateId], note: 'Put back after a removal was undone' })
          .then(refresh)
          .catch((cause: unknown) => show({ tone: 'danger', message: "They couldn't be put back", detail: errorMessage(cause) }));
      });
    } catch (cause) {
      show({ tone: 'danger', message: "They weren't removed", detail: errorMessage(cause) });
    }
  };

  return (
    <Card
      title="Talent pools"
      action={
        canEdit ? (
          <Button ref={triggerRef} variant="ghost" size="sm" icon={FolderPlus} onClick={toggle}>
            Add to pool
          </Button>
        ) : undefined
      }
    >
      {rows.length === 0 ? (
        <p className={styles.meta}>Not in any pool.</p>
      ) : (
        <ul className={styles.list}>
          {rows.map((pool) => (
            <li key={pool.membershipId} className={styles.person}>
              <Link to="/pools/$poolId" params={{ poolId: pool.poolId }} className={styles.name}>
                {pool.name}
              </Link>
              <span className={styles.meta}>
                since {formatDate(pool.addedAt)} · consent until {formatDate(pool.expiresAt)}
              </span>
              {canEdit && <Button variant="ghost" size="sm" icon={X} aria-label={`Remove from ${pool.name}`} onClick={() => void remove(pool.membershipId, pool.poolId, pool.name)} />}
            </li>
          ))}
        </ul>
      )}
      <Popover anchor={anchor} onClose={close} align="right" width={380} label="Add to pool">
        <AddToPoolPanel candidateIds={[candidateId]} who={name} onCancel={close} onDone={close} />
      </Popover>
    </Card>
  );
}
